import React, { useRef, useState } from "react";
import { faCaretLeft, faCaretRight } from "@fortawesome/pro-solid-svg-icons";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { tv } from "tailwind-variants";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link, useLocation } from "react-router-dom";
import { useTracking } from "@/requests/hooks/app";

interface IProps {
  tabs: ITab[];
}

const styles = tv({
  slots: {
    tabsContainer: "flex w-full flex-col",
    tabsScroll:
      "no-scrollbar flex w-full flex-row items-center gap-5 overflow-y-hidden overflow-x-scroll border-b-2 border-b-gray md:gap-[unset]",
    scrollIndicatorLeft:
      "flex h-full flex-shrink border-r-2 border-r-gray-dark pr-5 opacity-20 md:hidden",
    scrollIndicatorRight:
      "flex h-full flex-shrink border-l-2 border-l-gray-dark pl-5 opacity-20 md:hidden",
    tabPillContent:
      "no-scrollbar flex w-full flex-grow snap-mandatory flex-row gap-5 overflow-y-hidden overflow-x-scroll scroll-smooth md:gap-100 md:overflow-x-visible md:px-20",
    tabContainer:
      "group flex shrink-0 cursor-pointer snap-center flex-col text-gray-dark hover:text-secondary",
    tabTitle: "p-10 text-center",
    tabBorder: "h-3 w-full rounded-tl-sm rounded-tr-sm",
  },
  variants: {
    isIndicatorVisible: {
      true: { scrollIndicatorLeft: "opacity-100" },
      false: { scrollIndicatorRight: "opacity-100" },
    },
    isActive: {
      true: {
        tabContainer: "text-secondary",
        tabBorder: "!bg-primary",
      },
      false: {
        tabContainer: "text-gray-dark",
        tabBorder: "group-hover:bg-gray",
      },
    },
  },
});

export interface ITab {
  title: string;
  path: string;
  trackingId?: IGetEventProperties | string;
  isVisible?: boolean;
  checkIsActive?: () => boolean;
}

const Tabs = ({ tabs }: IProps) => {
  const { pathname } = useLocation();
  const [showScrollIndicator, setShowScrollIndicator] = useState<
    "left" | "both" | "right"
  >("right");
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const { mutate: sendTracking } = useTracking();
  const {
    tabsContainer,
    tabsScroll,
    scrollIndicatorLeft,
    scrollIndicatorRight,
    tabPillContent,
    tabContainer,
    tabTitle,
    tabBorder,
  } = styles();

  const handleClick = (trackingId?: IGetEventProperties | string) =>
    !!trackingId && sendTracking(trackingId);

  const handleScroll = (event: React.UIEvent<HTMLElement>) => {
    const {
      scrollWidth,
      scrollLeft: scrollPosition,
      clientWidth,
    } = event.currentTarget;
    const visibleDivWidth = scrollWidth - clientWidth - 0.5;

    if (scrollPosition === 0) {
      setShowScrollIndicator("right");
    }

    if (scrollPosition > 0 && scrollPosition < visibleDivWidth) {
      setShowScrollIndicator("both");
    }
    if (scrollPosition >= visibleDivWidth) {
      setShowScrollIndicator("left");
    }
  };

  const handleScrollClick = (scrollOffset: number) => {
    if (scrollRef.current) {
      scrollRef.current.scrollLeft += scrollOffset;
    }
  };

  const scrollLeft = () => handleScrollClick(-100);
  const scrollRight = () => handleScrollClick(100);

  const getIsActiveTab = (path: string, checkIsActive?: () => boolean) => {
    if (!!checkIsActive) return checkIsActive();

    return pathname.endsWith(path);
  };

  const renderTabs = () =>
    tabs.map(
      ({ title, path, trackingId, isVisible = true, checkIsActive }, i) => {
        if (!isVisible) return null;

        const isActive = getIsActiveTab(path, checkIsActive);

        return (
          <Link
            key={i}
            role="tab"
            to={path}
            title={title}
            data-testid={title}
            className={tabContainer({ isActive })}
            onClick={() => handleClick(trackingId)}>
            <div className={tabTitle()}>{title}</div>
            <div className={tabBorder({ isActive })} />
          </Link>
        );
      },
    );

  const renderCaret = (icon: IconProp) => (
    <FontAwesomeIcon className="h-15 w-15 text-gray-dark" icon={icon} />
  );

  const renderLeftIndicator = () => (
    <div
      className={scrollIndicatorLeft({
        isIndicatorVisible:
          showScrollIndicator === "left" || showScrollIndicator === "both",
      })}
      onClick={scrollLeft}>
      {renderCaret(faCaretLeft)}
    </div>
  );

  const renderRightIndicator = () => (
    <div
      className={scrollIndicatorRight({
        isIndicatorVisible:
          showScrollIndicator === "left" || showScrollIndicator === "both",
      })}
      onClick={scrollRight}>
      {renderCaret(faCaretRight)}
    </div>
  );

  const renderTabPills = () => (
    <div className={tabsScroll()}>
      {renderLeftIndicator()}
      <div className={tabPillContent()} onScroll={handleScroll} ref={scrollRef}>
        {renderTabs()}
      </div>
      {renderRightIndicator()}
    </div>
  );

  return <div className={tabsContainer()}>{renderTabPills()}</div>;
};

export default Tabs;
